// src/components/PickupAddressModal.jsx
// Shared modal for Add / Edit / Delete pickup (or drop) addresses.
// Used by CreateForwardOrder (Step 2) and CreateReverseOrder (Step 2).
import React, { useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../utils/apiUrls";

const INITIAL = {
  pickup_latitude: "",
  pickup_longitude: "",
  pickup_poc: "",
  pickup_organisation: "",
  pickup_address: "",
  pickup_district: "",
  pickup_city: "",
  pickup_state: "",
  pickup_landmark: "",
  pickup_poc_email: "",
  pickup_poc_contact_dialing_code: "",
  pickup_poc_contact_number: "",
  pickup_postal_code: "",
  pickup_country_code: "IN",
  pickup_address_type: "",
  pickup_hub_code: "",
  gst_number: "",
};

const REQUIRED = [
  "pickup_poc",
  "pickup_organisation",
  "pickup_address",
  "pickup_district",
  "pickup_city",
  "pickup_state",
  "pickup_postal_code",
  "pickup_poc_contact_number",
  "pickup_poc_email",
  "pickup_country_code",
];

/* ── Tiny field config so the form is driven by data ── */
const FIELDS = [
  { key: "pickup_poc", label: "Contact Person", icon: "👤", required: true, placeholder: "John Doe" },
  { key: "pickup_organisation", label: "Organization Name", icon: "🏢", required: true, placeholder: "Acme Corp" },
  { key: "pickup_address", label: "Street Address", icon: "📍", required: true, placeholder: "123 Main Street, Building A", fullWidth: true, textarea: true },
  { key: "pickup_city", label: "City", icon: "🏙️", required: true, placeholder: "Mumbai" },
  { key: "pickup_district", label: "District", icon: "🗺️", required: true, placeholder: "Mumbai Suburban" },
  { key: "pickup_state", label: "State", icon: "🌏", required: true, placeholder: "Maharashtra" },
  { key: "pickup_postal_code", label: "Postal Code", icon: "📮", required: true, placeholder: "400001", maxLength: 6 },
  { key: "pickup_landmark", label: "Landmark", icon: "🏛️", placeholder: "Near City Mall", fullWidth: true },
  { key: "pickup_poc_contact_number", label: "Phone Number", icon: "📱", required: true, placeholder: "9876543210", maxLength: 10 },
  { key: "pickup_poc_email", label: "Email Address", icon: "📧", required: true, placeholder: "contact@example.com", type: "email" },
  { key: "pickup_country_code", label: "Country Code", icon: "🌍", required: true, placeholder: "IN" },
  { key: "pickup_poc_contact_dialing_code", label: "Dialing Code", icon: "☎️", placeholder: "+91" },
  { key: "pickup_latitude", label: "Latitude", icon: "🧭", placeholder: "19.0760", type: "number" },
  { key: "pickup_longitude", label: "Longitude", icon: "🧭", placeholder: "72.8777", type: "number" },
  { key: "pickup_address_type", label: "Address Type", icon: "🏷️", placeholder: "Warehouse" },
  { key: "pickup_hub_code", label: "Hub Code", icon: "🏭", placeholder: "HUB001" },
  { key: "gst_number", label: "GST Number", icon: "📋", placeholder: "29ABCDE1234F1Z5", uppercase: true },
];

/* ═════════════════════════════ Delete Confirm Modal ═════════════════════════════ */
const DeleteConfirmModal = ({ open, onConfirm, onCancel, deleting }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">Delete Pickup Location</h3>
        </div>
        <p className="text-gray-600 mb-6 leading-relaxed">Are you sure you want to delete this pickup location? This action cannot be undone.</p>
        <div className="flex gap-3">
          <button onClick={onCancel} disabled={deleting} className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all">Cancel</button>
          <button onClick={onConfirm} disabled={deleting} className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-semibold transition-all shadow-lg flex items-center justify-center gap-2">
            {deleting ? (
              <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>Deleting...</>
            ) : "Delete"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ═════════════════════════════ MAIN EXPORT ═════════════════════════════ */
/**
 * @param {boolean}  open       Whether the modal is visible
 * @param {"add"|"edit"} mode   "add" or "edit"
 * @param {object|null} location Pickup location data when editing
 * @param {function} onClose    Called when the modal should close
 * @param {function} onSaved    Called after a successful add/edit/delete so the parent can refresh
 */
export default function PickupAddressModal({ open, mode = "add", location: loc, onClose, onSaved }) {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  // Populate form when opening for edit
  React.useEffect(() => {
    if (open && mode === "edit" && loc) {
      setForm({
        pickup_id: loc.pickup_address_id || loc.pickupId || null,
        pickup_latitude: loc.pickup_latitude ?? "",
        pickup_longitude: loc.pickup_longitude ?? "",
        pickup_poc: loc.pickup_poc ?? "",
        pickup_organisation: loc.pickup_organisation ?? "",
        pickup_address: loc.pickup_address ?? "",
        pickup_district: loc.pickup_district ?? "",
        pickup_city: loc.pickup_city ?? "",
        pickup_state: loc.pickup_state ?? "",
        pickup_landmark: loc.pickup_landmark ?? "",
        pickup_poc_email: loc.pickup_poc_email ?? "",
        pickup_poc_contact_dialing_code: loc.pickup_poc_contact_dialing_code ?? "",
        pickup_poc_contact_number: loc.pickup_poc_contact_number ?? "",
        pickup_postal_code: loc.pickup_postal_code ?? "",
        pickup_country_code: loc.pickup_country_code ?? "IN",
        pickup_address_type: loc.pickup_address_type ?? "",
        pickup_hub_code: loc.pickup_hub_code ?? "",
        gst_number: loc.pickup_gst_number ?? loc.gst_number ?? "",
      });
    } else if (open && mode === "add") {
      setForm({ ...INITIAL });
    }
  }, [open, mode, loc]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const k of REQUIRED) {
      if (!form[k]) {
        toast.error("Please fill all mandatory fields (marked with *).");
        return;
      }
    }
    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (mode === "add") delete payload.pickup_id;
      await axios.post(API.addOrUpdatePickupDetails(merchantId), [payload], {
        headers: { Authorization: authToken, "x-merchant-id": merchantId, "Content-Type": "application/json" },
      });
      toast.success(mode === "edit" ? "Pickup location updated!" : "Pickup location added!");
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(mode === "edit" ? "Failed to update pickup." : "Failed to add pickup.");
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-5xl relative max-h-[95vh] overflow-hidden animate-scale-in" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 p-4 sm:p-5 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                {mode === "edit" ? (
                  <><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>Edit Pickup Location</>
                ) : (
                  <><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>Add Pickup Location</>
                )}
              </h2>
              <p className="text-blue-100 text-xs mt-1">Fields marked with <span className="text-red-300">*</span> are required</p>
            </div>
            <button onClick={onClose} className="bg-white/20 hover:bg-white/30 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold transition-colors">×</button>
          </div>
        </div>

        {/* Form body */}
        <div className="overflow-y-auto max-h-[calc(95vh-180px)] p-4 sm:p-6 lg:p-8">
          <form onSubmit={handleSubmit} id="pickup-addr-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.fullWidth ? "sm:col-span-2" : ""}>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5">
                    {f.label} {f.required ? <span className="text-red-500">*</span> : <span className="text-gray-400 font-normal">(Optional)</span>}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none">{f.icon}</span>
                    {f.textarea ? (
                      <textarea
                        name={f.key}
                        value={form[f.key]}
                        onChange={handleChange}
                        rows={2}
                        className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm resize-none"
                        placeholder={f.placeholder}
                        required={f.required}
                      />
                    ) : (
                      <input
                        type={f.type || "text"}
                        step={f.type === "number" ? "any" : undefined}
                        name={f.key}
                        value={form[f.key]}
                        onChange={handleChange}
                        maxLength={f.maxLength}
                        className={`w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm ${f.uppercase ? "uppercase tracking-wider" : ""}`}
                        placeholder={f.placeholder}
                        required={f.required}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 sm:p-5 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-3 rounded-xl font-semibold transition-all text-sm">
            Cancel
          </button>
          <button type="submit" form="pickup-addr-form" disabled={submitting} className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm flex items-center justify-center gap-2">
            {submitting ? (
              <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>{mode === "edit" ? "Saving..." : "Adding..."}</>
            ) : mode === "edit" ? "Save Changes" : "Add Location"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Export the delete handler as a standalone utility ── */
export async function deletePickupAddress(pickupAddressId, onDone) {
  const merchantId = localStorage.getItem("merchantId");
  const authToken = localStorage.getItem("authToken");
  try {
    await axios.delete(API.deletePickupDetails(merchantId, pickupAddressId), {
      headers: { Authorization: authToken, "x-merchant-id": merchantId },
    });
    toast.success("Pickup location deleted!");
    onDone?.();
  } catch (err) {
    console.error(err);
    toast.error("Failed to delete pickup location.");
  }
}

/* ── Also export the DeleteConfirmModal for inline usage ── */
export { DeleteConfirmModal };
